import React from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ArrowLeft, ShieldCheck, FileText, WarningCircle } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "April 20, 2026";
const COMPANY = "Ekkol";
const JURISDICTION = "Greensboro, North Carolina, United States";
const SUPPORT_EMAIL = "support@ekkol.app";
const PRIVACY_EMAIL = "privacy@ekkol.app";

const SECTIONS = {
  terms: {
    title: "Terms of Service",
    icon: <FileText size={18}/>,
    body: () => (
      <>
        <h2>1. Acceptance</h2>
        <p>
          By accessing or using {COMPANY} (the &ldquo;Service&rdquo;), you agree to be bound by these Terms of Service
          (&ldquo;Terms&rdquo;). If you do not agree, you may not use the Service. {COMPANY} is operated as a sole
          proprietorship based in {JURISDICTION}.
        </p>

        <h2>2. Eligibility</h2>
        <p>
          You must be at least 13 years old to create an account. Users under 18 must have verifiable parental or
          guardian consent. {COMPANY} is not directed at children under 13, and we do not knowingly collect personal
          information from them.
        </p>

        <h2>3. Your account</h2>
        <p>
          You are responsible for maintaining the security of your account credentials and for all activity that occurs
          under your account. Notify us immediately at {SUPPORT_EMAIL} if you suspect unauthorized use.
        </p>

        <h2>4. Your content</h2>
        <p>
          You retain all rights to the decks, cards, documents, and study material you upload or create (&ldquo;User
          Content&rdquo;). By uploading User Content, you grant {COMPANY} a limited, non-exclusive, royalty-free license
          to store, display, and process that content solely to provide the Service to you. If you mark a deck as
          public, you also grant other users the right to view and import it.
        </p>
        <p>
          You are solely responsible for the legality of your User Content. Do not upload copyrighted material you do
          not own or have permission to use. {COMPANY} will remove infringing content upon valid notice.
        </p>

        <h2>5. Acceptable use</h2>
        <p>You agree NOT to:</p>
        <ul>
          <li>Use the Service for any unlawful purpose or to violate anyone&apos;s rights</li>
          <li>Attempt to gain unauthorized access to the Service, servers, or other users&apos; accounts</li>
          <li>Upload malware, viruses, or code intended to harm systems or users</li>
          <li>Scrape, copy, or mirror the Service without written permission</li>
          <li>Reverse-engineer or attempt to extract source code</li>
          <li>Use automated systems (bots, scripts) in a way that burdens the Service</li>
          <li>Resell or sublicense the Service without an Edu/Enterprise agreement</li>
          <li>Upload content that harasses, defames, or threatens any person or group</li>
        </ul>

        <h2>6. AI-generated output</h2>
        <p>
          {COMPANY} uses third-party AI models (including OpenAI and Anthropic models via Emergent) to chunk text,
          generate hints, score spoken answers, and convert questions. AI-generated output may contain errors,
          inaccuracies, or outdated information. <strong>You should not rely on AI-generated hints, summaries, or
          feedback as a substitute for professional advice</strong> in medicine, law, finance, safety-critical domains,
          or licensed-exam content. You are responsible for independently verifying any critical information.
        </p>

        <h2>7. Subscriptions &amp; billing</h2>
        <p>
          Paid plans (Plus, Edu, Enterprise) are billed monthly or annually in advance. You may cancel anytime from
          Profile → Plan &amp; billing; you will retain paid features until the end of the current billing period.
          Refunds are handled on a case-by-case basis — contact {SUPPORT_EMAIL} within 7 days of a charge to request
          review. All prices are in USD.
        </p>

        <h2>8. Termination</h2>
        <p>
          You may delete your account at any time from Profile → Danger zone. {COMPANY} may suspend or terminate your
          account if you violate these Terms, fail to pay fees, or engage in conduct harmful to the Service or other
          users, with or without notice where appropriate.
        </p>

        <h2>9. Disclaimers</h2>
        <p className="uppercase text-xs tracking-wide leading-relaxed">
          THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND,
          EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
          NON-INFRINGEMENT. {COMPANY} DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR ACHIEVE
          ANY SPECIFIC LEARNING OUTCOME.
        </p>

        <h2>10. Limitation of liability</h2>
        <p className="uppercase text-xs tracking-wide leading-relaxed">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY}, ITS OWNER, EMPLOYEES, CONTRACTORS, AND AGENTS SHALL NOT BE
          LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS,
          DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES. TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE AMOUNT YOU
          PAID TO {COMPANY} IN THE 12 MONTHS PRECEDING THE CLAIM, OR $100 USD, WHICHEVER IS GREATER.
        </p>

        <h2>11. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless {COMPANY}, its owner, and affiliates from any claims, damages,
          losses, or expenses arising from your use of the Service, your User Content, or your violation of these
          Terms.
        </p>

        <h2>12. Use while operating vehicles or machinery</h2>
        <p>
          The Service offers a &ldquo;hands-free&rdquo; study mode intended for stationary use (at a desk), walking,
          exercising, or riding as a passenger. <strong>You agree NOT to use the Service while driving, riding a
          bicycle, or operating any vehicle or machinery.</strong> You are solely responsible for your own safety
          and for complying with all applicable distracted-driving and distracted-operator laws. {COMPANY} is not
          liable for any injury, damage, citation, or loss resulting from use of the Service in violation of this
          section.
        </p>

        <h2>13. Changes to Terms</h2>
        <p>
          {COMPANY} may update these Terms from time to time. Material changes will be communicated by email or
          in-app notice at least 14 days before taking effect. Continued use after the effective date constitutes
          acceptance.
        </p>

        <h2>14. Governing law</h2>
        <p>
          These Terms are governed by the laws of the State of North Carolina, without regard to conflict-of-laws
          principles. Any dispute shall be resolved exclusively in the state or federal courts located in Guilford
          County, North Carolina, and you consent to the jurisdiction of those courts.
        </p>

        <h2>15. Contact</h2>
        <p>
          Questions about these Terms: {SUPPORT_EMAIL}
        </p>
      </>
    ),
  },
  privacy: {
    title: "Privacy Policy",
    icon: <ShieldCheck size={18}/>,
    body: () => (
      <>
        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy explains how {COMPANY} collects, uses, and protects your personal information. We are
          committed to handling your data transparently and securely.
        </p>

        <h2>2. Information we collect</h2>
        <p><strong>Account data</strong> (when you sign in with Google): name, email address, profile picture.</p>
        <p><strong>Content you create</strong>: decks, cards, text you upload, documents you import.</p>
        <p><strong>Usage data</strong>: study attempts, scores, timestamps, which features you use.</p>
        <p><strong>Technical data</strong>: IP address, browser type, device type (collected automatically for
          security and analytics).</p>
        <p><strong>What we do NOT collect</strong>: We do NOT store your speech audio. Speech recognition happens in
          your browser; only the resulting text (your spoken answer) is sent to our servers for scoring.</p>

        <h2>3. How we use your information</h2>
        <ul>
          <li>To provide the Service (read your decks, score your answers, generate hints)</li>
          <li>To personalize your experience (apply your profile preferences, track progress)</li>
          <li>To improve the Service (aggregate, anonymized usage analytics)</li>
          <li>To send account emails (receipts, security alerts, important updates)</li>
          <li>To provide support when you contact us</li>
          <li>To detect and prevent fraud, abuse, or violations of our Terms</li>
        </ul>
        <p>
          <strong>We do not sell your data. We do not run ads. We do not train AI models on your content.</strong>
        </p>

        <h2>4. AI processing</h2>
        <p>
          To provide AI features (concept matching, hints, chunking), we send the relevant text to third-party AI
          providers (OpenAI, Anthropic, Google) via the Emergent integrations platform. These providers process the
          text transiently and do not store it or use it to train models.
        </p>

        <h2>5. Data sharing</h2>
        <p>We share data ONLY in these circumstances:</p>
        <ul>
          <li>With service providers who help us operate (hosting, AI inference, email delivery) under strict
            confidentiality</li>
          <li>To comply with legal obligations (court order, subpoena, law enforcement request)</li>
          <li>To protect rights, safety, or property of {COMPANY}, our users, or the public</li>
          <li>With your explicit consent</li>
          <li>In connection with a business transfer (merger, acquisition), with notice to affected users</li>
        </ul>

        <h2>6. Cookies &amp; tracking</h2>
        <p>
          {COMPANY} uses essential cookies (for sign-in sessions) and privacy-respecting analytics (PostHog) to
          understand usage patterns. You can opt out of analytics via your browser&apos;s Do-Not-Track setting or by
          contacting us.
        </p>

        <h2>7. Your rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access</strong> — Download your data anytime from Profile → Download my data</li>
          <li><strong>Correct</strong> — Edit your profile info, decks, and cards from within the app</li>
          <li><strong>Delete</strong> — Profile → Danger zone → Delete my account (permanent within 30 days)</li>
          <li><strong>Export</strong> — JSON export of all your content</li>
          <li><strong>Object</strong> — Email {PRIVACY_EMAIL} to restrict certain processing</li>
        </ul>
        <p>
          EU / UK residents: GDPR rights apply. California residents: CCPA rights apply. Contact {PRIVACY_EMAIL} with
          requests; we respond within 30 days.
        </p>

        <h2>8. Data security</h2>
        <p>
          We use industry-standard encryption in transit (HTTPS/TLS) and at rest. Access to production data is
          restricted to authorized personnel. We conduct regular security reviews. However, no system is 100%
          secure — we encourage you to use strong, unique sign-in credentials.
        </p>

        <h2>9. Data retention</h2>
        <p>
          Account data is retained while your account is active. Study history (attempts, scores) is retained for
          analytics and streak features. Deleted accounts are fully purged from backups within 30 days, except where
          law requires longer retention.
        </p>

        <h2>10. International users</h2>
        <p>
          Ekkol servers are based in the United States. By using the Service, you consent to the transfer and
          processing of your data in the US, which may have different privacy laws than your country of residence.
        </p>

        <h2>11. Children</h2>
        <p>
          Ekkol is designed for users 13 and older. We do not knowingly collect information from children under 13.
          If you believe a child under 13 has created an account, contact {PRIVACY_EMAIL} immediately.
        </p>

        <h2>12. Changes to this policy</h2>
        <p>
          We&apos;ll notify you of material changes by email or in-app notice at least 14 days before they take
          effect. The &ldquo;Effective date&rdquo; at the top of this page will be updated.
        </p>

        <h2>13. Contact</h2>
        <p>Privacy questions: {PRIVACY_EMAIL} · General support: {SUPPORT_EMAIL}</p>
      </>
    ),
  },
  disclaimer: {
    title: "Disclaimer & Acceptable Use",
    icon: <WarningCircle size={18}/>,
    body: () => (
      <>
        <h2>1. Educational tool only</h2>
        <p>
          Ekkol is a study aid designed to help you memorize content you provide. Our AI features (hints, concept
          matching, summarization) assist learning but are not substitutes for qualified instruction, professional
          advice, or official exam preparation materials.
        </p>

        <h2>2. AI output is not professional advice</h2>
        <p>
          AI-generated hints, explanations, summaries, or scoring feedback must not be relied upon as:
        </p>
        <ul>
          <li><strong>Medical advice</strong> — consult a licensed physician</li>
          <li><strong>Legal advice</strong> — consult a licensed attorney</li>
          <li><strong>Financial advice</strong> — consult a licensed financial advisor</li>
          <li><strong>Safety-critical information</strong> — (e.g., emergency procedures, pharmaceuticals, aviation)</li>
          <li><strong>Official certification content</strong> — always cross-reference with the certifying body&apos;s
            official materials (e.g., AXELOS for ITIL, PMI for PMP, AWS for AWS certifications)</li>
        </ul>
        <p>
          AI can hallucinate, misremember, or produce subtly incorrect information. <strong>You are responsible for
          verifying any critical content independently.</strong>
        </p>

        <h2>3. Do not use while operating vehicles or machinery</h2>
        <p>
          Ekkol&apos;s hands-free mode is designed for <strong>stationary use, walking, exercising, or riding as
          a passenger</strong>. <strong>The Service is NOT intended or authorized for use while driving a vehicle,
          riding a bicycle, or operating machinery.</strong> You agree that:
        </p>
        <ul>
          <li>You will not launch, interact with, or listen to the Service while you are the operator of any vehicle or machinery</li>
          <li>You will comply with all distracted-operator and distracted-driving laws in your jurisdiction</li>
          <li>You assume full responsibility for your own safety and the safety of others around you</li>
          <li>{COMPANY} is not liable for any accident, injury, citation, or damage resulting from misuse of the Service</li>
        </ul>

        <h2>4. No guaranteed outcomes</h2>
        <p>
          {COMPANY} makes no guarantee that using Ekkol will result in passing any exam, mastering any skill, or
          achieving any specific learning outcome. Individual results depend on your effort, prior knowledge, quality
          of input material, and many other factors outside our control.
        </p>

        <h2>5. Your content, your responsibility</h2>
        <p>
          When you upload or paste content into Ekkol, you affirm that you have the right to do so. Do not upload:
        </p>
        <ul>
          <li>Copyrighted material you don&apos;t own (licensed textbooks, paid exam dumps, etc.)</li>
          <li>Confidential information (trade secrets, classified documents, PII belonging to others)</li>
          <li>Content that is harassing, defamatory, or unlawful</li>
          <li>Malicious code or links</li>
        </ul>
        <p>
          Violations may result in account termination and, in egregious cases, referral to law enforcement.
        </p>

        <h2>6. Third-party links and services</h2>
        <p>
          The Service may link to third-party websites, services, or resources. {COMPANY} does not endorse or take
          responsibility for the content, accuracy, or practices of third parties.
        </p>

        <h2>7. Reporting issues</h2>
        <p>
          Report abuse, copyright violations, or safety concerns to {SUPPORT_EMAIL}. We take these reports seriously
          and act promptly.
        </p>

        <h2>8. Effective date</h2>
        <p>Effective {EFFECTIVE_DATE}.</p>
      </>
    ),
  },
};

export default function Legal() {
  const { doc = "terms" } = useParams();
  const section = SECTIONS[doc] || SECTIONS.terms;

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 fade-in">
        <Link to="/" className="text-sm link-underline inline-flex items-center gap-1 mb-8">
          <ArrowLeft size={14}/> Back
        </Link>

        <div className="mb-4 flex items-center gap-2 eyebrow text-[color:var(--oxford)]">
          {section.icon} {COMPANY} · Legal
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mb-3">
          {section.title}
        </h1>
        <p className="text-xs text-[color:var(--ink-muted)] font-mono mb-10">
          Effective {EFFECTIVE_DATE} · Operated by {COMPANY} · {JURISDICTION}
        </p>

        <div className="flex gap-3 mb-10 flex-wrap border-y rule py-3">
          <Link to="/legal/terms" className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 ${doc === "terms" ? "bg-[color:var(--ink)] text-white" : "hover:bg-[color:var(--surface-alt)]"}`} data-testid="legal-tab-terms">Terms</Link>
          <Link to="/legal/privacy" className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 ${doc === "privacy" ? "bg-[color:var(--ink)] text-white" : "hover:bg-[color:var(--surface-alt)]"}`} data-testid="legal-tab-privacy">Privacy</Link>
          <Link to="/legal/disclaimer" className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 ${doc === "disclaimer" ? "bg-[color:var(--ink)] text-white" : "hover:bg-[color:var(--surface-alt)]"}`} data-testid="legal-tab-disclaimer">Disclaimer &amp; AUP</Link>
        </div>

        <article className="legal-prose" data-testid={`legal-content-${doc}`}>
          {section.body()}
        </article>

        <div className="mt-16 p-6 border rule bg-white text-sm text-[color:var(--ink-muted)]">
          <p className="mb-2">
            <strong className="text-[color:var(--ink)]">Plain-English summary:</strong> Ekkol is a study tool.
            Use it at a desk, on a walk, or as a passenger. <strong>Don&apos;t use it while you&apos;re driving or
            operating machinery — that&apos;s on you.</strong> We protect your data. AI answers can be wrong —
            double-check important stuff. If you disagree with anything, email {SUPPORT_EMAIL}.
          </p>
          <p className="text-xs">
            These documents are provided in good faith but are not a substitute for personalized legal advice. We
            reserve the right to have these reviewed and updated by qualified counsel.
          </p>
        </div>
      </main>
    </div>
  );
}
